/**
 * Product Compare
 * Lets shoppers select products on a collection/search page,
 * then view a side-by-side comparison table in a modal.
 *
 * Depends on:
 *  - sections/product-compare.liquid   (renders bar + modal + settings)
 *  - snippets/product-compare-checkbox.liquid (checkbox on each product card)
 */
(function () {
    const STORAGE_KEY = 'shopify-product-compare';
    const MODE_KEY = 'shopify-product-compare-mode';
    const closeIconBar = "<svg xmlns=\"http://www.w3.org/2000/svg\" width=\"10\" height=\"10\" viewBox=\"0 0 10 10\" fill=\"none\">\n    <path d=\"M9.5 0.5L0.5 9.5M0.5 0.5L9.5 9.5\" stroke=\"black\" stroke-linecap=\"round\" stroke-linejoin=\"round\" />\n</svg>\n"

    class ProductCompare {
        constructor(root) {
            this.root = root;
            this.maxProducts = parseInt(root.dataset.maxProducts, 10) || 4;
            this.settings = {
                showPrice: root.dataset.showPrice === 'true',
                showVendor: root.dataset.showVendor === 'true',
                showType: root.dataset.showType === 'true',
                showDescription: root.dataset.showDescription === 'true',
                showVariants: root.dataset.showVariants !== 'false',
                descriptionLimit: parseInt(root.dataset.descriptionLimit, 10) || 20,
            };
            this.swatchColorMap = this.parseSwatchColors(root.dataset.swatchColors);
            this.swatchFileUrlTemplate = root.dataset.swatchFileUrlTemplate || '';

            this.bar = root.querySelector('[data-compare-bar]');
            this.barItems = root.querySelector('[data-compare-bar-items]');
            this.countEl = root.querySelector('[data-compare-count]');
            this.openBtn = root.querySelector('[data-compare-open]');
            this.clearBtn = root.querySelector('[data-compare-clear]');

            this.modal = root.querySelector('[data-compare-modal]');
            this.tableBody = root.querySelector('[data-compare-table-body]');
            this.closeEls = root.querySelectorAll('[data-compare-close]');

            this.state = this.loadState();
            this.productCache = new Map();

            this.bindEvents();
            this.bindToggle();
            this.restoreMode();
            this.syncCheckboxes();
            this.renderBar();
        }

        loadState() {
            try {
                const raw = sessionStorage.getItem(STORAGE_KEY);
                return raw ? JSON.parse(raw) : [];
            } catch (e) {
                return [];
            }
        }

        saveState() {
            try {
                sessionStorage.setItem(STORAGE_KEY, JSON.stringify(this.state));
            } catch (e) {
                /* storage unavailable, ignore */
            }
        }

        bindEvents() {
            // Delegate checkbox changes (product cards render dynamically via
            // infinite scroll / filtering, so we listen on the document).
            document.addEventListener('change', (e) => {
                const input = e.target.closest('[data-compare-input]');
                if (!input) return;
                this.handleCheckboxChange(input);
            });

            this.openBtn.addEventListener('click', () => this.openModal());
            this.clearBtn.addEventListener('click', () => this.clearAll());

            this.closeEls.forEach((el) =>
                el.addEventListener('click', () => this.closeModal())
            );

            document.addEventListener('keydown', (e) => {
                if (e.key === 'Escape') this.closeModal();
            });
        }

        // "Compare Products" toggle switch(es) — there may be more than one
        // instance rendered on the page (e.g. desktop + mobile toolbar).
        bindToggle() {
            document.addEventListener('click', (e) => {
                const toggle = e.target.closest('[data-compare-toggle]');
                if (!toggle) return;
                const isActive = document.body.classList.toggle('compare-mode-active');
                this.setToggleState(isActive);
                this.renderBar();
                try {
                    sessionStorage.setItem(MODE_KEY, isActive ? 'true' : 'false');
                } catch (err) {
                    /* storage unavailable, ignore */
                }
            });
        }

        setToggleState(isActive) {
            document.querySelectorAll('[data-compare-toggle]').forEach((toggle) => {
                toggle.setAttribute('aria-checked', isActive ? 'true' : 'false');
            });
        }

        restoreMode() {
            let isActive = false;
            try {
                isActive = sessionStorage.getItem(MODE_KEY) === 'true';
            } catch (e) {
                isActive = false;
            }
            document.body.classList.toggle('compare-mode-active', isActive);
            this.setToggleState(isActive);
        }

        getCardContainer(input) {
            return (
                input.closest('.card-wrapper') ||
                input.closest('.showcase-product-card') ||
                input.closest('article') ||
                input.closest('li') ||
                input.closest('.grid__item') ||
                input.closest('div')
            );
        }

        getCardImage(input) {
            const container = this.getCardContainer(input);
            if (!container) return '';
            const img = container.querySelector('img');
            return img ? img.currentSrc || img.src : '';
        }

        getCardTitle(input) {
            const container = this.getCardContainer(input);
            if (!container) return '';
            const titleEl = container.querySelector('[class*="title"], h3, h2');
            return titleEl ? titleEl.textContent.trim() : '';
        }

        handleCheckboxChange(input) {
            const id = input.value;
            const handle = input.dataset.handle;

            if (input.checked) {
                if (this.state.length >= this.maxProducts) {
                    input.checked = false;
                    alert(`You can only compare up to ${this.maxProducts} products.`);
                    return;
                }
                if (!this.state.find((p) => p.id === id)) {
                    this.state.push({
                        id,
                        handle,
                        image: this.getCardImage(input),
                        title: this.getCardTitle(input),
                    });
                }
            } else {
                this.state = this.state.filter((p) => p.id !== id);
            }

            this.saveState();
            this.syncCheckboxes();
            this.renderBar();
        }

        // Keep all checkboxes on the page (across pagination) in sync with state,
        // and disable unchecked boxes once the max is reached. Also keeps the
        // wrapper's "is-selected" class in sync so it stays visible without hover.
        syncCheckboxes() {
            const selectedIds = new Set(this.state.map((p) => p.id));
            document.querySelectorAll('[data-compare-input]').forEach((input) => {
                const isChecked = selectedIds.has(input.value);
                input.checked = isChecked;
                input.disabled = !isChecked && this.state.length >= this.maxProducts;
                const wrapper = input.closest('[data-compare-checkbox]');
                if (wrapper) wrapper.classList.toggle('is-selected', isChecked);
            });
        }

        renderBar() {
            const count = this.state.length;
            this.countEl.textContent = count;
            this.openBtn.disabled = count < 2;

            const isModeActive = document.body.classList.contains('compare-mode-active');
            this.bar.classList.toggle('is-visible', isModeActive);

            let slotsHtml = '';
            for (let i = 0; i < this.maxProducts; i += 1) {
                const item = this.state[i];
                if (item) {
                    slotsHtml += `
              <div class="product-compare-bar__slot product-compare-bar__slot--filled" data-bar-item="${item.id}">
                <img src="${item.image}" alt="${item.title || item.handle}">
                <button type="button" class="product-compare-bar__remove" data-remove-id="${item.id}" aria-label="Remove ${item.title || item.handle}">${closeIconBar}</button>
              </div>
            `;
                } else {
                    slotsHtml += '<div class="product-compare-bar__slot product-compare-bar__slot--empty"></div>';
                }
            }

            this.barItems.innerHTML = slotsHtml;

            this.barItems.querySelectorAll('[data-remove-id]').forEach((btn) => {
                btn.addEventListener('click', () => this.removeProduct(btn.dataset.removeId));
            });
        }

        removeProduct(id) {
            this.state = this.state.filter((p) => p.id !== id);
            this.saveState();
            this.syncCheckboxes();
            this.renderBar();
            if (this.modal.classList.contains('is-open')) {
                this.renderTable();
            }
        }

        clearAll() {
            this.state = [];
            this.saveState();
            this.syncCheckboxes();
            this.renderBar();
            this.closeModal();
        }

        async fetchProduct(handle) {
            if (this.productCache.has(handle)) {
                return this.productCache.get(handle);
            }
            const res = await fetch(`/products/${handle}.js`);
            if (!res.ok) return null;
            const data = await res.json();
            this.productCache.set(handle, data);
            return data;
        }

        // Build price HTML directly from the product's own .js payload
        // (already fetched in fetchProduct) instead of fetching the full
        // product page a second time. Faster (no extra request + no
        // full-page DOM parse per product) and correct (no risk of
        // grabbing a `.price` element from an unrelated section of the
        // page, e.g. a recommended-products block that repeats on every
        // product page — which is what was causing every row to show
        // the same price).
        formatMoney(cents) {
            const moneyFormat = this.root.dataset.moneyFormat || '${{amount}}';
            const amount = (cents / 100).toFixed(2);
            return moneyFormat.replace(/\{\{\s*\w+\s*\}\}/, amount);
        }

        getNormalizedTags(product) {
            if (Array.isArray(product.tags)) {
                return product.tags.join(',').toLowerCase();
            }

            return String(product.tags || '').toLowerCase();
        }

        shouldShowRequestPrice(product) {
            const normalizedTags = this.getNormalizedTags(product);
            return (
                product.price === 0 ||
                (normalizedTags.includes('request-product') && normalizedTags.includes('no-show-price'))
            );
        }

        buildPriceHtml(product) {
            if (this.shouldShowRequestPrice(product)) {
                return '<span class="product-compare-table__price product-compare-table__price--request">Request For Quote</span>';
            }

            const hasRange = product.price_varies || product.price_min !== product.price_max;
            const onSale =
                product.compare_at_price &&
                product.compare_at_price > product.price &&
                !hasRange;

            if (hasRange) {
                return `<span class="product-compare-table__price">From ${this.formatMoney(product.price_min)}</span>`;
            }

            if (onSale) {
                return `
            <span class="product-compare-table__price product-compare-table__price--sale">${this.formatMoney(product.price)}</span>
            <span class="product-compare-table__price product-compare-table__price--compare-at">${this.formatMoney(product.compare_at_price)}</span>
          `;
            }

            return `<span class="product-compare-table__price">${this.formatMoney(product.price)}</span>`;
        }

        truncate(text, wordLimit) {
            const words = (text || '').replace(/<[^>]*>/g, '').split(/\s+/).filter(Boolean);
            if (words.length <= wordLimit) return words.join(' ');
            return words.slice(0, wordLimit).join(' ') + '…';
        }

        async openModal() {
            this.modal.classList.add('is-open');
            this.modal.setAttribute('aria-hidden', 'false');
            document.body.style.overflow = 'hidden';
            await this.renderTable();
        }

        closeModal() {
            this.modal.classList.remove('is-open');
            this.modal.setAttribute('aria-hidden', 'true');
            document.body.style.overflow = '';
        }

        async renderTable() {
            if (this.state.length === 0) {
                this.tableBody.innerHTML =
                    '<tr><td class="product-compare-table__empty">No products selected.</td></tr>';
                return;
            }

            this.tableBody.innerHTML =
                '<tr><td class="product-compare-table__empty">Loading…</td></tr>';

            const products = (
                await Promise.all(this.state.map((p) => this.fetchProduct(p.handle)))
            ).filter(Boolean);

            if (products.length === 0) {
                this.tableBody.innerHTML =
                    '<tr><td class="product-compare-table__empty">Unable to load products.</td></tr>';
                return;
            }

            const rows = [];

            // "Product Info" row: image, remove (x), title, View Details button
            rows.push(`
          <tr class="product-compare-table__info-row">
            <th>Product Info</th>
            ${products
                    .map(
                        (p) => `
              <td class="product-compare-table__product-cell">
                <div class="product-compare-table__product-inner">
                  <button type="button" class="product-compare-table__remove" data-remove-id="${p.id}" aria-label="Remove ${p.title}">${closeIconBar}</button>
                  <img src="${p.featured_image || (p.images && p.images[0]) || ''}" alt="${p.title}">
                  <p class="product-compare-table__title">${p.title}</p>
                  <a class="product-compare-table__view-btn" href="/products/${p.handle}" target="_blank" rel="noopener">View Details</a>
                </div>
              </td>
            `
                    )
                    .join('')}
          </tr>
        `);

            if (this.settings.showDescription) {
                rows.push(`
            <tr>
              <th>Description</th>
              ${products
                        .map(
                            (p) =>
                                `<td><div class="product-compare-table__description-cell">${this.truncate(p.description, this.settings.descriptionLimit)}</div></td>`
                        )
                        .join('')}
            </tr>
          `);
            }

            if (this.settings.showPrice) {
                rows.push(`
            <tr>
              <th>Price</th>
              ${products
                        .map((p) => `<td>${this.buildPriceHtml(p)}</td>`)
                        .join('')}
            </tr>
          `);
            }

            if (this.settings.showVendor) {
                rows.push(`
            <tr>
              <th>Brand</th>
              ${products.map((p) => `<td>${p.vendor || '—'}</td>`).join('')}
            </tr>
          `);
            }

            if (this.settings.showType) {
                rows.push(`
            <tr>
              <th>Type</th>
              ${products.map((p) => `<td>${p.type || '—'}</td>`).join('')}
            </tr>
          `);
            }

            if (this.settings.showVariants) {
                rows.push(`
            <tr>
              <th>Product Variants</th>
              ${products.map((p) => `<td>${this.renderVariantsCell(p)}</td>`).join('')}
            </tr>
          `);
            }

            this.tableBody.innerHTML = rows.join('');

            this.tableBody.querySelectorAll('[data-remove-id]').forEach((btn) => {
                btn.addEventListener('click', () => this.removeProduct(btn.dataset.removeId));
            });
        }

        // Finds a Color/Colour-type option (matches names like "Color",
        // "Colour", "Select Color", "Choose a Colour", etc.) and returns its
        // distinct values. Handles options as either plain strings or
        // objects like { name, values }.
        getOptionName(option) {
            if (typeof option === 'string') return option;
            return option?.name || option?.value || '';
        }

        getColorValues(product) {
            if (!product.options || !product.variants) return [];
            const idx = product.options.findIndex((option) =>
                /colou?r/i.test(this.getOptionName(option))
            );
            if (idx === -1) return [];
            const key = `option${idx + 1}`;
            return [...new Set(product.variants.map((v) => v[key]).filter(Boolean))];
        }

        normalizeColorName(name) {
            return String(name || '').trim().toLowerCase();
        }

        parseSwatchColors(raw) {
            if (!raw) return new Map();

            let value = raw;
            try {
                value = JSON.parse(raw);
            } catch (e) {
                value = raw;
            }

            const map = new Map();
            String(value)
                .split(/\r?\n/)
                .map((line) => line.trim())
                .filter(Boolean)
                .forEach((line) => {
                    const separatorIndex = line.indexOf(':');
                    if (separatorIndex === -1) return;

                    const name = line.slice(0, separatorIndex).trim();
                    const colorValue = line.slice(separatorIndex + 1).trim();
                    if (!name || !colorValue) return;

                    map.set(this.normalizeColorName(name), colorValue);
                });

            return map;
        }

        isValidCssColor(value) {
            if (!value || typeof CSS === 'undefined' || !CSS.supports) return false;
            return CSS.supports('color', value);
        }

        getSwatchFileUrl(fileName) {
            if (!this.swatchFileUrlTemplate || !fileName) return '';
            const normalizedFileName = String(fileName).trim().toLowerCase().replace(/\s+/g, '-');
            return this.swatchFileUrlTemplate.replace('__SWATCH_FILE__', encodeURIComponent(normalizedFileName));
        }

        getSwatchStyle(name) {
            const mappedValue = this.swatchColorMap.get(this.normalizeColorName(name));

            if (mappedValue) {
                const colorCodes = mappedValue.split('#');
                const colorNumber = colorCodes.length;

                if (colorNumber === 2) {
                    return `background-color: #${colorCodes[1]};`;
                }

                if (colorNumber === 3) {
                    return `background: linear-gradient(135deg, #${colorCodes[1]} 50%, #${colorCodes[2]} 50%);`;
                }

                if (colorNumber === 4) {
                    return `background: linear-gradient(to right, #${colorCodes[1]} 33%, #${colorCodes[2]} 33%, #${colorCodes[2]} 66%, #${colorCodes[3]} 66%);`;
                }

                const fileUrl = this.getSwatchFileUrl(colorCodes[0]);
                if (fileUrl) {
                    return `background-image: url("${fileUrl}");`;
                }
            }

            if (this.isValidCssColor(name)) {
                return `background-color: ${name};`;
            }

            const compactName = String(name).toLowerCase().replace(/[^a-z]/g, '');
            if (this.isValidCssColor(compactName)) {
                return `background-color: ${compactName};`;
            }

            return '';
        }

        renderVariantsCell(product) {
            if (!product.options || product.options.length === 0) return 'N/A';

            // Shopify omits real options when a product only has the
            // auto-generated "Title" / "Default Title" variant.
            const isDefaultOnly =
                product.options.length === 1 &&
                this.getOptionName(product.options[0]).toLowerCase() === 'title';
            if (isDefaultOnly) return 'N/A';

            const blocks = product.options
                .map((option, idx) => {
                    const optionName = this.getOptionName(option);
                    const key = `option${idx + 1}`;

                    // Prefer explicit values from the option object if present,
                    // otherwise derive distinct values from the variants list.
                    let values = Array.isArray(option?.values) ? option.values : null;
                    if (!values) {
                        values = [
                            ...new Set(product.variants.map((v) => v[key]).filter(Boolean)),
                        ];
                    }
                    if (!values || values.length === 0) return '';

                    const isColor = /colou?r/i.test(optionName);

                    if (isColor) {
                        const swatches = values
                            .map(
                                (name) =>
                                    `<span class="product-compare-table__swatch" style="${this.getSwatchStyle(name)}" title="${name}"></span>`
                            )
                            .join('');
                        const label = values.length === 1 ? '1 color' : `${values.length} colors`;
                        return `
                <div class="product-compare-table__variant-group">
                  <span class="product-compare-table__variant-label">${label}</span>
                  <div class="product-compare-table__swatches">${swatches}</div>
                </div>
              `;
                    }

                    return `
              <div class="product-compare-table__variant-group">
                <span class="product-compare-table__variant-label">${optionName}:</span>
                ${values.join(', ')}
              </div>
            `;
                })
                .filter(Boolean);

            return blocks.length ? blocks.join('') : 'N/A';
        }
    }

    document.addEventListener('DOMContentLoaded', () => {
        const root = document.getElementById('product-compare-root');
        if (root) new ProductCompare(root);
    });
})();
